import React, {useEffect, useRef, useState} from "react";
import { v4 as uuidv4 } from "uuid";
import LogoFrukt from "./Components/LogoFrukt";
import LogoBarnas from "./Components/LogoBarnas";

interface MenuItem {
    url: string;
    name: string;
}

interface FeaturedItems {
    url: { 
        uri: string; 
    };
    text: string;
    imageModel: {
        imageUrl: string;
    };
}

interface HeaderProps {
    data: {
        mainMenu: MenuItem[];
        hamburgerMenu: MenuItem[];
        featuredItems: FeaturedItems[];
        featuredTitle: string;
        displaySearch: boolean;
        site: string;
    }
}
const Navbar: React.FC<HeaderProps> = ({data}) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const toggleMenu = () => {
        setMenuIsOpen(!menuIsOpen);
    }

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setMenuIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    
    return (
        <>
            <header className="flex flex-col w-full pb-4">
                <nav className="w-full border-b  py-2 border-brown-primary" aria-label="Hovedmeny">
                    <div className="container flex flex-col">
                        <div className="flex items-center w-full gap-8">
                            {data.site === "frukt" && (
                                <LogoFrukt />
                            )}

                            {data.site === "barnas" && (
                                <LogoBarnas />
                            )}
                            <div className="w-full flex justify-between items-center">
                                <ul className="hidden w-full laptop:flex flex-wrap items-start gap-4">
                                    {data.mainMenu.map((item) => (
                                        <li key={uuidv4()}>
                                            <a
                                                title={item.name}
                                                href={item.url}
                                                className="menu-item leading-loose text-xl font-semibold"
                                            >
                                                {item.name}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                                <div className={`flex w-full justify-end gap-5 ${data.displaySearch ? "w-[137px]" : "w-[65px]"}`}>
                                    {data.displaySearch && (
                                        <a href="/sok/" className="flex py-2 text-sm laptop:text-base font-semibold rounded-md focus-visible:outline outline-2 focus-visible:outline-red-secondary">
                                            <div className="flex border-brown-25 border-b-2 hover:border-red-secondary gap-2 pb-1">
                                                <div className="icon-search" aria-hidden="true"></div>
                                                Søk
                                            </div>
                                        </a>
                                    )}
                                    <button
                                        ref={buttonRef}
                                        type="button"
                                        className="group py-2 text-sm laptop:text-base font-semibold rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-secondary"
                                        onClick={toggleMenu}
                                    >
                                        <div className="flex border-brown-25 border-b-2 hover:border-red-secondary  gap-2 pb-1">
                                            <div className={menuIsOpen ? "icon-x" : "icon-menu"} aria-hidden="true"></div>
                                            {menuIsOpen ? "Lukk" : "Meny"}
                                        </div>
                                    </button>
                                </div>                                
                            </div>
                        </div>
                    </div>
                </nav>
                <nav ref={ref}
                    aria-label="rullegardin-meny"
                    className={`${
                        menuIsOpen ? "flex" : "hidden"
                    } flex-col pt-4 gap-5 laptop:flex-row-reverse laptop:items-start laptop:container laptop:p-0 laptop:pt-6`}
                >
                    <div className="container w-full h-full pt-4 pb-6 laptop:w-1/3 desktop:w-1/4">
                        <div className="flex flex-col gap-4 tablet:flex-row laptop:shrink-0">
                            <ul className="flex flex-col gap-3 tablet:w-1/2 laptop:hidden">
                                {data.mainMenu.map((item) => (
                                    <li key={uuidv4()}>
                                        <a 
                                            href={item.url}
                                           title={item.name}
                                           className="menu-item font-semibold"
                                        >
                                            {item.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <ul className="flex flex-col gap-3 laptop:items-end laptop:w-full laptop:justify-start laptop:gap-5">
                                {data.hamburgerMenu.map((item) => (
                                    <li key={uuidv4()}>
                                        <a 
                                            href={item.url} 
                                            title={item.name} 
                                            className="menu-item"
                                        >
                                            {item.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-brown-primary pt-4 pb-6 w-full laptop:bg-brown-25 laptop:w-2/3 desktop:w-3/4">
                        <div className="flex flex-col gap-2 container">
                            <h2 className="text-xl font-semibold">{data.featuredTitle}</h2>
                            <ul className="grid grid-cols-3 gap-4 tablet:gap-5">
                                {data.featuredItems.map((item) => (
                                    <li key={uuidv4()}>
                                        <a
                                            title={item.text}
                                            href={item.url.uri}
                                            className="flex flex-col gap-3 rounded-lg focus:outline outline-2 focus:outline-red-secondary"
                                        >
                                            <div className="overflow-hidden rounded-lg">
                                                <picture>
                                                    <source media="(min-width: 1280px)"
                                                            srcSet={`${item.imageModel.imageUrl}__w=1024_h=768_autoCrop=true`}/>
                                                    <source media="(min-width: 1024px)"
                                                            srcSet={`${item.imageModel.imageUrl}__w=640_h=480_autoCrop=true`}/>
                                                    <source media="(min-width: 768px)"
                                                            srcSet={`${item.imageModel.imageUrl}__w=320_h=240_autoCrop=true`}/>
                                                    <img
                                                    className="object-cover bg-brown-50 rounded-lg tablet:h-40 laptop:h-32 desktop:h-60 transition-transform duration-300 ease-in-out hover:scale-110"
                                                    srcSet={`${item.imageModel.imageUrl}__w=240_h=180_autoCrop=true`}
                                                    sizes="(max-width: 1024px) 100vw, 1024px"
                                                    width="767"
                                                    height="431" alt={item.text} />
                                                </picture>
                                            </div>
                                            <p className="laptop:leading-loose text-base laptop:text-xl font-semibold">{item.text}</p>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </nav>
                <div
                    className={`${
                        menuIsOpen ? "" : "hidden"
                    } laptop:border-b border-brown-primary w-full`}
                ></div>
            </header>
        </>
    )
};
export default Navbar;